import { apiCustomer, apiOrders } from "@/api"
import {
  couponBenefitOf,
  couponKindOf,
  couponOfferBenefit,
  couponOfferOf,
  couponRewardOf,
  couponRuleShapeOf,
  type CouponBenefit,
  type CouponKind,
  type CouponOffer,
  type CouponReward,
} from "@/lib/coupon-benefit"
import { couponWorksSomeTimeToday, formatCouponUsageWindows, isCouponAvailableNow } from "@/lib/coupon-availability"
import { invalidCouponMessage } from "@/lib/discount-evaluation/coupon-copy"
import type { EvaluationReason } from "@/lib/discount-evaluation/types"
import { currentGuestToken, hasRecoverableGuestSession } from "@/lib/guest-session"
import { dropPendingCouponCode, forgetKeptCoupons, keepCouponForLater } from "@/lib/pending-coupon"
import type { CouponAvailability } from "@/types/external/coupon-response"
import { isAxiosError } from "axios"

/**
 * Whether a received code works right now. `usable` may still wait on the bag
 * (minimum, required items); `unavailable-now` is outside its weekly window;
 * `invalid` will not work at all; `unknown` means the check itself failed.
 */
export type CouponReadiness =
  | { status: "usable" }
  | { status: "unavailable-now"; availability: CouponAvailability | null }
  | { status: "invalid"; message: string }
  | { status: "unknown" }

type CouponLookup = { found: false } | { found: true; coupon: Record<string, unknown> }

const VALIDATION_REASON: Record<string, EvaluationReason> = {
  COUPON_NOT_FOUND: "EVALUATION_REASON_NOT_FOUND",
  COUPON_INACTIVE: "EVALUATION_REASON_INACTIVE",
  COUPON_EXPIRED: "EVALUATION_REASON_EXPIRED",
  COUPON_MAX_USES_REACHED: "EVALUATION_REASON_USAGE_LIMIT_REACHED",
  COUPON_NOT_ELIGIBLE: "EVALUATION_REASON_NOT_ASSIGNED_TO_CUSTOMER",
  COUPON_ALREADY_USED: "EVALUATION_REASON_ALREADY_USED_BY_CUSTOMER",
  COUPON_NOT_FIRST_PURCHASE: "EVALUATION_REASON_FIRST_PURCHASE_ONLY",
  COUPON_CANCELLED: "EVALUATION_REASON_CANCELLED",
}

const RETRYABLE_CATEGORIES = new Set(["INTERNAL", "EXTERNAL_SERVICE"])

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function availabilityOf(coupon: Record<string, unknown> | null): CouponAvailability | null {
  const raw = coupon && isObject(coupon.availability) ? coupon.availability : null
  if (!raw || !Array.isArray(raw.blocks)) return null
  const blocks = raw.blocks.filter(isObject).map((block) => ({
    days: Array.isArray(block.days) ? block.days.filter((day): day is string => typeof day === "string") : [],
    schedules: (Array.isArray(block.schedules) ? block.schedules : []).filter(isObject).map((shift) => ({
      start: typeof shift.start === "string" ? shift.start : "",
      end: typeof shift.end === "string" ? shift.end : "",
    })),
  }))
  return { blocks, redeemOnlyWithinAvailability: raw.redeemOnlyWithinAvailability === true }
}

async function validateForSession(merchantId: string, code: string): Promise<CouponReadiness | null> {
  if (!currentGuestToken() && !hasRecoverableGuestSession()) return null
  try {
    const { data } = await apiCustomer.post<unknown>(`/merchants/${merchantId}/coupons/validate`, {
      code,
      purchaseAmount: 0,
    })
    if (!isObject(data)) return null
    if (data.isValid === true) return { status: "usable" }
    const errorCode = typeof data.errorCode === "string" ? data.errorCode : ""
    if (typeof data.category === "string" && RETRYABLE_CATEGORIES.has(data.category)) return null
    if (errorCode === "COUPON_OUTSIDE_AVAILABILITY") return { status: "unavailable-now", availability: null }
    if (errorCode === "COUPON_MIN_PURCHASE_NOT_MET") return { status: "usable" }
    const reason = VALIDATION_REASON[errorCode]
    return reason ? { status: "invalid", message: invalidCouponMessage(code, reason) } : null
  } catch {
    return null
  }
}

async function lookupCoupon(merchantId: string, code: string): Promise<CouponLookup | null> {
  try {
    const { data } = await apiOrders.get<unknown>(
      `/merchants/${merchantId}/coupons/by-code/${encodeURIComponent(code)}`,
    )
    return isObject(data) ? { found: true, coupon: data } : null
  } catch (error) {
    return isAxiosError(error) && error.response?.status === 404 ? { found: false } : null
  }
}

function readinessFromLookup(code: string, lookup: CouponLookup, now: Date): CouponReadiness {
  if (!lookup.found) return { status: "invalid", message: invalidCouponMessage(code, "EVALUATION_REASON_NOT_FOUND") }
  const { coupon } = lookup
  const status = typeof coupon.status === "string" ? coupon.status.toUpperCase() : ""
  if (status === "EXPIRED") return { status: "invalid", message: invalidCouponMessage(code, "EVALUATION_REASON_EXPIRED") }
  if (status === "EXHAUSTED") {
    return { status: "invalid", message: invalidCouponMessage(code, "EVALUATION_REASON_USAGE_LIMIT_REACHED") }
  }
  if (coupon.active === false || status === "INACTIVE") {
    return { status: "invalid", message: invalidCouponMessage(code, "EVALUATION_REASON_INACTIVE") }
  }
  const expiresAt = typeof coupon.expirationDate === "string" ? Date.parse(coupon.expirationDate) : Number.NaN
  if (Number.isFinite(expiresAt) && expiresAt > 0 && expiresAt < now.getTime()) {
    return { status: "invalid", message: invalidCouponMessage(code, "EVALUATION_REASON_EXPIRED") }
  }
  const availability = availabilityOf(coupon)
  if (availability && !isCouponAvailableNow(availability, now)) return { status: "unavailable-now", availability }
  return { status: "usable" }
}

/** What the coupon's public record says it gives, for a received code the customer is about to use. */
export type CouponDetails = { benefit: CouponBenefit }

/** A received code's readiness plus its details and terms, null when the public record could not be read. */
export type ReceivedCoupon = { readiness: CouponReadiness; details: CouponDetails | null; terms: CouponTerms | null }

/**
 * Checks a received code the way the checkout will: coupon-service's verdict
 * for the session customer (customer-service-gateway `coupons/validate`,
 * tenant clock), plus the coupon's public record for its weekly window and
 * benefit. A visitor without a session gets the public record alone, judged
 * on this device's clock — a hint the checkout re-checks.
 */
export async function checkReceivedCoupon(code: string, merchantId: string, now: Date = new Date()): Promise<ReceivedCoupon> {
  const [verdict, lookup] = await Promise.all([validateForSession(merchantId, code), lookupCoupon(merchantId, code)])
  const terms = lookup?.found ? termsOf(lookup.coupon) : null
  if (terms) termsCache.set(`${merchantId}:${code}`, Promise.resolve(terms))
  const details = terms ? { benefit: terms.benefit } : null
  const availability = terms?.availability ?? null
  if (verdict?.status === "unavailable-now") return { readiness: { status: "unavailable-now", availability }, details, terms }
  if (verdict) return { readiness: verdict, details, terms }
  return { readiness: lookup ? readinessFromLookup(code, lookup, now) : { status: "unknown" }, details, terms }
}

/** A coupon's public terms as the bag and the reveal sheet show them: its kind and type-first offer (null on old records, which read from the rules), what it gives, how much, the minimum order (cents, 0 = none), its weekly window, expiry and first-purchase rule. */
export type CouponTerms = {
  kind: CouponKind | null
  offer: CouponOffer | null
  benefit: CouponBenefit
  reward: CouponReward
  minPurchaseCents: number
  availability: CouponAvailability | null
  expiresAt: string | null
  firstPurchaseOnly: boolean
  conditional: boolean
  maxDiscountCents: number
}

function termsOf(coupon: Record<string, unknown>): CouponTerms {
  const minimum = typeof coupon.minPurchaseValue === "number" && Number.isFinite(coupon.minPurchaseValue) ? coupon.minPurchaseValue : 0
  const expiresAt = typeof coupon.expirationDate === "string" && Date.parse(coupon.expirationDate) > 0 ? coupon.expirationDate : null
  const offer = couponOfferOf(coupon.offer)
  const read = offer ? couponOfferBenefit(offer) : { benefit: couponBenefitOf(coupon.ruleSet), reward: couponRewardOf(coupon.ruleSet) }
  const shape = couponRuleShapeOf(coupon.ruleSet)
  return {
    kind: offer?.kind ?? couponKindOf(coupon.kind),
    offer,
    ...read,
    minPurchaseCents: Math.max(0, Math.round(minimum * 100)),
    availability: availabilityOf(coupon),
    expiresAt,
    firstPurchaseOnly: coupon.firstPurchaseOnly === true,
    conditional: offer ? false : shape.conditional,
    maxDiscountCents: shape.maxDiscountCents,
  }
}

const termsCache = new Map<string, Promise<CouponTerms | null>>()

/** A code's public terms, read once per page load; null when the record is missing or unreadable (a failed read is asked again next time). */
export function readCouponTerms(merchantId: string, code: string): Promise<CouponTerms | null> {
  const key = `${merchantId}:${code}`
  const cached = termsCache.get(key)
  if (cached) return cached
  const pending = lookupCoupon(merchantId, code).then((lookup) => {
    if (!lookup) termsCache.delete(key)
    return lookup?.found ? termsOf(lookup.coupon) : null
  })
  termsCache.set(key, pending)
  return pending
}

/** What a code gives, from its public record ({@link readCouponTerms} without the rest). */
export async function readCouponDetails(merchantId: string, code: string): Promise<CouponDetails | null> {
  const terms = await readCouponTerms(merchantId, code)
  return terms ? { benefit: terms.benefit } : null
}

/** {@link checkReceivedCoupon} without the details. */
export async function checkCouponReadiness(code: string, merchantId: string, now: Date = new Date()): Promise<CouponReadiness> {
  return (await checkReceivedCoupon(code, merchantId, now)).readiness
}

const KEPT_LINE = "Ele fica guardado e será aplicado sozinho quando estiver valendo."

/**
 * What to tell the customer about a received code that does not work now;
 * null when it does (or could not be checked), so the caller keeps its own
 * line. Never claims the code is applied.
 */
export function couponNotUsableMessage(code: string, readiness: CouponReadiness, now: Date = new Date()): string | null {
  if (readiness.status === "invalid") return readiness.message
  if (readiness.status !== "unavailable-now") return null
  const windows = formatCouponUsageWindows(readiness.availability)
  if (!windows) return `Seu cupom ${code} não foi aplicado porque não vale agora. ${KEPT_LINE}`
  const when = couponWorksSomeTimeToday(readiness.availability, now) ? "neste horário" : "hoje"
  return `Seu cupom ${code} não foi aplicado porque não vale ${when}. Você pode usar ${windows}. ${KEPT_LINE}`
}

/**
 * {@link couponNotUsableMessage} for a won prize: the same verdict told about
 * the prize, never its code; null when it works now (or could not be checked).
 */
export function prizeNotUsableMessage(readiness: CouponReadiness, now: Date = new Date()): string | null {
  if (readiness.status === "invalid") return "Este prêmio não pode mais ser usado."
  if (readiness.status !== "unavailable-now") return null
  const windows = formatCouponUsageWindows(readiness.availability)
  const kept = "Ele fica guardado e entra sozinho no pedido quando estiver valendo."
  if (!windows) return `Seu prêmio não vale agora. ${kept}`
  const when = couponWorksSomeTimeToday(readiness.availability, now) ? "neste horário" : "hoje"
  return `Seu prêmio não vale ${when}. Você pode usar ${windows}. ${kept}`
}

/**
 * Files a received code by its verdict: a usable one is kept so it survives a
 * login redirect and reaches checkout, one outside its window is kept across
 * visits so it applies on a day it works; one that will never work leaves the
 * stash and the kept list, so the checkout does not raise it again.
 */
export function settleReceivedCoupon(code: string, merchantId: string, readiness: CouponReadiness): void {
  if (readiness.status === "usable" || readiness.status === "unavailable-now") keepCouponForLater(code, merchantId)
  if (readiness.status === "invalid") {
    dropPendingCouponCode(code, merchantId)
    forgetKeptCoupons([code], merchantId)
  }
}
